import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { marked } from 'marked';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { FileUploadHandlerEvent } from 'primeng/fileupload';
import { Popover } from 'primeng/popover';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { SeedGenericIconComponent } from '@/app/components/icons/seed-generic-icon.component';
import { SeedIconNativeComponent } from '@/app/components/icons/seed-icon-native.component';
import { WarningCirculeIconComponent } from '@/app/components/icons/warning-circule-icon.component';
import {
  InquiryStreamV2Event,
  InquiryStreamV2Request,
  InquiryStreamV2Service,
  InquiryUiAction,
} from '@/app/core/services/inquiry-stream-v2.service';
import { LanguageInitializerService } from '@/app/core/services/language-initializer.service';
import { ExtendedLandViewModel, PlantationService } from '@/app/core/services/plantation.service';
import { UserService } from '@/app/core/services/user.service';
import { SkeletonComponent } from '@/app/features/shared/components/skeleton/skeleton.component';
import {
  CreateInquiryCommandV2,
  FileParameter,
  FileProxy,
  InquiryCorrespondenceDTO,
  InquiryProxy,
  InquiryStatus,
  InquiryViewModel,
  InventoryProxy,
  LookupProxy,
} from '@/app/proxy/proxies';
import { UploadFileAcceptedFileTypes, UploadFileMaxFileSize } from '@/app/shared/consts/comms-consts';
import { DkTypingComponent } from '@/app/shared/components/dk-typing/dk-typing.component';
import { DateFormatOptions, UtcToLocalDatePipe } from '@/app/shared/pipes/utc-to-local-date.pipe';
import { SharedModule } from '@/app/shared/shared.module';
import {
  AiTurnRecord,
  SupportTicketAnalyticsService,
  SupportTicketChatSurface,
} from '../support-ticket-analytics.service';
import {
  InquiryTypeQuickAction,
  mapInquiryTypesToQuickActions,
} from '../support-ticket-inquiry-types.util';
import { INQUIRY_STATUS, isInquiryThinking, isInquiryUnread } from '../support-ticket-inquiry-status.util';

export enum SupportTicketChatState {
  Welcome = 'welcome',
  Chat = 'chat',
}

interface IChatPlantation {
  plantationId: string;
  name: string;
  image: string;
  cropColor: string;
  cropName: string;
  landName: string;
  totalAreaDisplay: string;
  harvestDateDisplay: string;
  selected: boolean;
}

interface IChatDevice {
  deviceId: string;
  name: string;
  productionId: string;
}

interface PendingUiAction {
  actionType: string;
  reason?: string;
  messageId: string;
}

interface ResolvedUiActionSelection {
  actionType: string;
  selectedId: string;
}

interface PendingRetry {
  createFirst: boolean;
  request: InquiryStreamV2Request | null;
  content: string;
  uploadedFileId?: string;
  fileType?: string;
}

/** AI Message Sent payload held back until `inquiry_CreateV2` hands us a chat id. */
interface PendingMessageSent {
  message: string;
  previousAiMessageLength: number;
  customMessage: boolean;
  photoAdded: boolean;
}

const MAX_STREAM_ATTEMPTS = 3;
const STREAM_RETRY_BACKOFF_MS = [500, 1000, 2000];
const OPTIMISTIC_USER_PREFIX = 'local-user-';
const OPTIMISTIC_AI_PREFIX = 'local-ai-';

const SUPPORTED_UI_ACTION_TYPES = ['plantation.request_selection', 'inventory.request_selection'];

const TYPEWRITER_TICK_MS = 16;
const TYPEWRITER_BASE_CHARS_PER_TICK = 2;
const TYPEWRITER_MAX_CATCHUP_LAG = 240;
const THINKING_POLL_INTERVAL_MS = 3000;

function createEmptyInquiry(): InquiryViewModel {
  const inquiry = new InquiryViewModel();
  inquiry.inquiryCorrespondences = [];
  return inquiry;
}

@Component({
  selector: 'dk-support-ticket-chat',
  imports: [
    SharedModule,
    TranslateModule,
    SkeletonComponent,
    SeedGenericIconComponent,
    SeedIconNativeComponent,
    WarningCirculeIconComponent,
    UtcToLocalDatePipe,
    DkTypingComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.less',
  providers: [InquiryProxy, FileProxy, LookupProxy, InventoryProxy],
})
export class ChatComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('messageContainer') messagesContainer!: ElementRef;
  @ViewChild('uploadFilePopover') uploadFilePopover!: Popover;

  @Input() inquiryId: string | undefined = undefined;
  @Input() forcePanelType: SupportTicketChatState = SupportTicketChatState.Welcome;
  /** Which surface hosts this panel, reported as `ai state` on AI analytics events. */
  @Input() surface: SupportTicketChatSurface = 'page';
  @Output() chatCreated = new EventEmitter<string>();
  @Output() chatActivity = new EventEmitter<string>();
  @Output() inquiryStatusChanged = new EventEmitter<{ inquiryId: string; status: InquiryStatus }>();

  SupportTicketChatState = SupportTicketChatState;
  panelType: SupportTicketChatState = SupportTicketChatState.Welcome;

  userFirstName = '';

  quickActions: InquiryTypeQuickAction[] = [];
  loadingInquiryTypes = false;

  selectedQuickActionKey: string | null = null;
  readonly greetingTimestamp: DateTime = DateTime.local();

  inquiry: InquiryViewModel = createEmptyInquiry();
  loadingInquiry = false;
  loadError = false;

  renderedContents: { [key: string]: SafeHtml } = {};

  isStreaming = false;
  showTypingIndicator = false;
  sendingMessage = false;
  private streamSubscription: Subscription | null = null;
  private thinkingPollSubscription: Subscription | null = null;
  private currentOptimisticAiId: string | null = null;
  private currentOptimisticUserId: string | null = null;
  private aiBuffer = '';
  private firstChunkReceived = false;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private displayedContent = '';
  private typewriterTimer: ReturnType<typeof setInterval> | null = null;
  private streamClosed = false;

  streamErrorMessageId: string | null = null;
  pendingRetry: PendingRetry | null = null;

  private pendingMessageSent: PendingMessageSent | null = null;
  private currentTurn: AiTurnRecord | null = null;
  /** True once the turn's content is known and only the server reconcile is outstanding. */
  private currentTurnAwaitingReconcile = false;
  /** True when the running thinking poll is watching a turn this instance never streamed. */
  private pollResumedTurn = false;

  private suppressNextInquiryIdChange = false;

  pendingUiAction: PendingUiAction | null = null;
  resolvedUiActionSelections: { [messageId: string]: ResolvedUiActionSelection } = {};
  plantationsGroupedByLands: SelectItemGroup<IChatPlantation>[] = [];
  devices: SelectItem<IChatDevice>[] = [];
  loadingPlantations = false;
  loadingDevices = false;
  selectedUiActionId: string | null = null;

  maxFileSize = UploadFileMaxFileSize;
  acceptedFileTypes = UploadFileAcceptedFileTypes;
  uploadedFileId = '';
  uploadedFile: File = new File([], '');
  private inquiryFileUploadTypeId = '';
  messageHasUploadedFile = false;

  messageForm: FormGroup = new FormGroup({
    message: new FormControl('', [Validators.minLength(1)]),
  });

  headerDateFormat: DateFormatOptions = {
    showTimeAgo: false,
    simpleFormat: { month: 'long', day: '2-digit', year: 'numeric' },
  };

  constructor(
    private fileProxy: FileProxy,
    private inquiryProxy: InquiryProxy,
    private lookupProxy: LookupProxy,
    private plantationService: PlantationService,
    private inventoryProxy: InventoryProxy,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private inquiryStreamV2Service: InquiryStreamV2Service,
    private translateService: TranslateService,
    private languageInitializerService: LanguageInitializerService,
    private analytics: SupportTicketAnalyticsService
  ) {}

  ngOnInit(): void {
    this.userFirstName = this.userService.getUserFirstName();
    this.loadFileUploadTypeId();
    this.loadInquiryTypesLookup();
    this.panelType = this.inquiryId ? SupportTicketChatState.Chat : this.forcePanelType;
    if (this.inquiryId) {
      this.loadInquiry();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['forcePanelType'] && !changes['forcePanelType'].firstChange && !this.inquiryId) {
      this.panelType = this.forcePanelType;
      if (this.forcePanelType === SupportTicketChatState.Welcome) {
        this.resetConversationState();
      }
    }

    if (changes['inquiryId'] && !changes['inquiryId'].firstChange) {
      if (this.suppressNextInquiryIdChange) {
        this.suppressNextInquiryIdChange = false;
        return;
      }

      this.cancelStream();
      if (this.inquiryId) {
        this.panelType = SupportTicketChatState.Chat;
        this.loadInquiry();
      } else {
        this.panelType = this.forcePanelType;
        this.resetConversationState();
      }
    }
  }

  resetToWelcome(): void {
    this.cancelStream();
    this.panelType = SupportTicketChatState.Welcome;
    this.resetConversationState();
  }

  ngOnDestroy(): void {
    this.cancelStream();
  }

  selectQuickAction(action: InquiryTypeQuickAction): void {
    if (this.inquiryId || this.sendingMessage || this.isStreaming || !!this.pendingRetry) {
      return;
    }

    this.panelType = SupportTicketChatState.Chat;
    this.selectedQuickActionKey = action.key;
    this.clearMessage();

    const content = action.label;
    // Held until the create call returns, because the chat id doesn't exist yet.
    this.pendingMessageSent = {
      message: content,
      previousAiMessageLength: this.analytics.lastAiMessageLength(this.inquiry.inquiryCorrespondences),
      customMessage: false,
      photoAdded: false,
    };
    this.appendOptimisticUserMessage(content, undefined, undefined);
    this.appendOptimisticAiPlaceholder();
    this.scrollToBottom(true);
    this.createInquiryAndStream(content, undefined, undefined, action.inquiryTypeId);
  }

  private resetConversationState(): void {
    this.inquiry = createEmptyInquiry();
    this.renderedContents = {};
    this.loadError = false;
    this.loadingInquiry = false;
    this.pendingUiAction = null;
    this.selectedUiActionId = null;
    this.resolvedUiActionSelections = {};
    this.streamErrorMessageId = null;
    this.pendingRetry = null;
    this.selectedQuickActionKey = null;
    this.clearMessage();
  }

  private detectSelectedQuickAction(): void {
    const first = this.inquiry.inquiryCorrespondences?.[0];
    if (!first?.isByMe || !first.inquiryContent) {
      this.selectedQuickActionKey = null;
      return;
    }
    const normalized = first.inquiryContent.trim().toLowerCase();
    const matched = this.quickActions.find((action) => action.label.trim().toLowerCase() === normalized);
    this.selectedQuickActionKey = matched?.key ?? null;
  }

  private loadFileUploadTypeId(): void {
    this.lookupProxy.lookup_GetFileTypes().subscribe({
      next: (res) => {
        this.inquiryFileUploadTypeId = res.result?.find((item) => item.value === 'Inquiry')?.key || '';
      },
    });
  }

  private loadInquiryTypesLookup(): void {
    this.loadingInquiryTypes = true;
    this.lookupProxy.lookup_GetInquiryTypesV2().subscribe({
      next: (res) => {
        this.quickActions = mapInquiryTypesToQuickActions(res.result);
        this.loadingInquiryTypes = false;
        if (this.inquiryId) {
          this.detectSelectedQuickAction();
        }
      },
      error: () => {
        this.quickActions = [];
        this.loadingInquiryTypes = false;
      },
    });
  }

  private get defaultInquiryTypeId(): string {
    return this.quickActions[0]?.inquiryTypeId ?? '';
  }

  loadInquiry(withoutLoading: boolean = false): void {
    if (!this.inquiryId) {
      return;
    }
    if (!withoutLoading) {
      this.loadingInquiry = true;
      this.loadError = false;
    }

    this.inquiryProxy.inquiry_Get(this.inquiryId).subscribe({
      next: (res) => {
        this.applyLoadedInquiry(res.result || new InquiryViewModel());
      },
      error: () => {
        this.loadingInquiry = false;
        if (!withoutLoading) {
          this.loadError = true;
        }
        // Reconcile failed, so report the turn with only the stream's own signal.
        this.flushCurrentTurn();
      },
    });
  }

  retryLoadInquiry(): void {
    this.loadInquiry();
  }

  private applyLoadedInquiry(serverInquiry: InquiryViewModel): void {
    const sorted = (serverInquiry.inquiryCorrespondences || [])
      .filter((message) => message != null)
      .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());

    this.inquiry = serverInquiry;
    this.inquiry.inquiryCorrespondences = this.normalizeCorrespondences(sorted);
    this.detectSelectedQuickAction();
    this.processMarkdownForMessages();
    this.syncUiActionState();
    this.syncOngoingGenerationState(serverInquiry);
    this.loadingInquiry = false;
    if (isInquiryUnread(serverInquiry.status)) {
      this.emitInquiryStatus(this.inquiryId, INQUIRY_STATUS.Read);
    }
    if (this.currentTurnAwaitingReconcile) {
      this.flushCurrentTurn(serverInquiry.ticketCount ?? 0);
    }
    this.scrollToBottom(true);
  }

  private isAwaitingAiResponse(inquiry: InquiryViewModel): boolean {
    if (!isInquiryThinking(inquiry.status)) {
      return false;
    }

    const messages = inquiry.inquiryCorrespondences ?? [];
    if (messages.length === 0) {
      return false;
    }

    const last = messages[messages.length - 1];

    if (last.isByMe || last.correspondenceType === 'User') {
      return true;
    }

    if (last.correspondenceType === 'AI' && !last.inquiryContent?.trim()) {
      return true;
    }

    if (last.correspondenceType === 'AI' && last.inquiryContent?.trim() && last.uiAction) {
      const action = this.findSupportedUiAction(last.uiAction?.actions);
      const selectedId = last.uiActionSelection?.selectedId ?? this.resolvedUiActionSelections[last.id]?.selectedId;
      if (action && selectedId) {
        return true;
      }
    }

    if (last.correspondenceType === 'AI' && last.inquiryContent?.trim()) {
      return false;
    }

    return true;
  }

  private syncOngoingGenerationState(inquiry: InquiryViewModel): void {
    if (!this.isAwaitingAiResponse(inquiry)) {
      this.stopThinkingPoll();
      return;
    }

    this.restoreGeneratingUi();
    if (!this.thinkingPollSubscription) {
      this.startThinkingPoll();
    }
  }

  private restoreGeneratingUi(): void {
    this.showTypingIndicator = true;
    this.isStreaming = true;

    if (this.shouldAppendOptimisticAiPlaceholder()) {
      this.appendOptimisticAiPlaceholder();
    }
  }

  private shouldAppendOptimisticAiPlaceholder(): boolean {
    if (this.currentOptimisticAiId) {
      return false;
    }

    const messages = this.inquiry.inquiryCorrespondences ?? [];
    const last = messages[messages.length - 1];
    if (!last) {
      return true;
    }

    if (last.correspondenceType === 'AI' && !last.inquiryContent?.trim()) {
      return false;
    }

    return true;
  }

  private startThinkingPoll(): void {
    if (!this.inquiryId) {
      return;
    }

    // Only a turn this instance never streamed needs the poll to report it; a
    // streamed turn is already reported (or about to be) by the stream paths.
    this.pollResumedTurn = !this.currentTurn && !this.currentTurnAwaitingReconcile;

    this.thinkingPollSubscription = timer(0, THINKING_POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.inquiryProxy.inquiry_Get(this.inquiryId!)))
      .subscribe({
        next: (res) => {
          const serverInquiry = res.result;
          if (!serverInquiry) {
            return;
          }
          this.handleThinkingPollResult(serverInquiry);
        },
      });
  }

  private stopThinkingPoll(): void {
    if (this.thinkingPollSubscription) {
      this.thinkingPollSubscription.unsubscribe();
      this.thinkingPollSubscription = null;
    }
  }

  private handleThinkingPollResult(serverInquiry: InquiryViewModel): void {
    const sorted = (serverInquiry.inquiryCorrespondences || [])
      .filter((message) => message != null)
      .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());
    const normalizedCorrespondences = this.normalizeCorrespondences(sorted);
    const inquiryForCheck = Object.assign(new InquiryViewModel(), serverInquiry, {
      inquiryCorrespondences: normalizedCorrespondences,
    });

    if (this.isAwaitingAiResponse(inquiryForCheck)) {
      return;
    }

    this.trackPolledTurnReceived(serverInquiry, normalizedCorrespondences);

    this.stopThinkingPoll();
    this.removeOptimisticAiMessage();
    this.resetStreamingFlags();

    if (this.inquiryId) {
      this.emitInquiryStatus(this.inquiryId, INQUIRY_STATUS.Read);
      this.chatActivity.emit(this.inquiryId);
    }

    this.inquiry = serverInquiry;
    this.inquiry.inquiryCorrespondences = normalizedCorrespondences;
    this.detectSelectedQuickAction();
    this.processMarkdownForMessages();
    this.syncUiActionState();
    this.scrollToBottom(true);
  }

  /**
   * Reports the response for a turn that was already generating when this panel
   * opened - the user left mid-answer and came back, so there was no stream to
   * observe and the poll is the only place the reply becomes visible.
   */
  private trackPolledTurnReceived(
    serverInquiry: InquiryViewModel,
    correspondences: InquiryCorrespondenceDTO[]
  ): void {
    if (!this.pollResumedTurn) {
      return;
    }
    this.pollResumedTurn = false;

    const lastAiMessage = [...correspondences]
      .reverse()
      .find((message) => message.correspondenceType === 'AI');

    this.analytics.trackAiMessageReceived(
      {
        chatId: this.inquiryId ?? serverInquiry.id ?? '',
        surface: this.surface,
        ticketCountBefore: this.inquiry.ticketCount ?? 0,
        uiActionType: this.findSupportedUiAction(lastAiMessage?.uiAction?.actions)?.actionType,
        aiMessageLength: this.analytics.lastAiMessageLength(correspondences),
        errored: false,
      },
      serverInquiry.ticketCount ?? 0
    );
  }

  private normalizeCorrespondences(correspondences: InquiryCorrespondenceDTO[]): InquiryCorrespondenceDTO[] {
    this.resolvedUiActionSelections = {};
    const visible: InquiryCorrespondenceDTO[] = [];

    for (const message of correspondences) {
      if (this.isHiddenCorrespondence(message)) {
        const selection = message.uiActionSelection;
        if (selection?.selectedId && selection?.type) {
          const aiMessage = this.findPrecedingUiActionMessage(visible, selection.type);
          if (aiMessage) {
            this.resolvedUiActionSelections[aiMessage.id] = {
              actionType: selection.type,
              selectedId: selection.selectedId,
            };
            aiMessage.uiActionSelection = selection;
          }
        }
        continue;
      }
      visible.push(message);
    }

    return visible;
  }

  private isHiddenCorrespondence(message: InquiryCorrespondenceDTO): boolean {
    if (!message.isByMe || message.correspondenceType === 'AI') {
      return false;
    }
    const content = (message.inquiryContent ?? '').trim();
    return !content && !message.fileUrl;
  }

  private findPrecedingUiActionMessage(
    messages: InquiryCorrespondenceDTO[],
    actionType: string
  ): InquiryCorrespondenceDTO | undefined {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.correspondenceType !== 'AI' || !message.uiAction) {
        continue;
      }
      const action = this.findSupportedUiAction(message.uiAction?.actions);
      if (action?.actionType === actionType) {
        return message;
      }
    }
    return undefined;
  }

  private syncUiActionState(): void {
    this.pendingUiAction = null;
    this.selectedUiActionId = null;
    const messages = this.inquiry.inquiryCorrespondences || [];
    const lastMessage = messages[messages.length - 1];

    let needsPlantations = false;
    let needsDevices = false;

    for (const message of messages) {
      if (message.correspondenceType !== 'AI' || !message.uiAction) {
        continue;
      }
      const action = this.findSupportedUiAction(message.uiAction?.actions);
      if (!action) {
        continue;
      }

      if (action.actionType === 'plantation.request_selection') {
        needsPlantations = true;
      } else if (action.actionType === 'inventory.request_selection') {
        needsDevices = true;
      }

      const selectedId = message.uiActionSelection?.selectedId ?? this.resolvedUiActionSelections[message.id]?.selectedId;
      if (selectedId) {
        this.resolvedUiActionSelections[message.id] = { actionType: action.actionType, selectedId };
      } else if (message === lastMessage) {
        this.pendingUiAction = { actionType: action.actionType, reason: action.reason, messageId: message.id };
      }
    }

    if (needsPlantations) {
      this.ensurePlantationsLoaded();
    }
    if (needsDevices) {
      this.ensureDevicesLoaded();
    }
  }

  private findSupportedUiAction(actions: InquiryUiAction[] | undefined): InquiryUiAction | undefined {
    return (actions || []).find((a) => SUPPORTED_UI_ACTION_TYPES.includes(a.actionType));
  }

  sendMessage(): void {
    const content: string = (this.messageForm.get('message')?.value || '').trim();
    if (!content || !this.canSendMessage) {
      return;
    }
    const uploadedFileId = this.messageHasUploadedFile ? this.uploadedFileId : undefined;
    const fileType = this.messageHasUploadedFile ? 'Image' : undefined;
    const fileUrl = this.messageHasUploadedFile ? this.getUploadedFilePreviewUrl() : undefined;
    const photoAdded = this.messageHasUploadedFile;
    // Read before the optimistic messages land, so this is the AI reply that
    // preceded this message rather than the empty placeholder about to be added.
    const previousAiMessageLength = this.analytics.lastAiMessageLength(this.inquiry.inquiryCorrespondences);

    this.appendOptimisticUserMessage(content, fileType, fileUrl);
    this.appendOptimisticAiPlaceholder();
    this.clearMessage();
    this.scrollToBottom(true);

    if (!this.inquiryId) {
      this.pendingMessageSent = {
        message: content,
        previousAiMessageLength,
        customMessage: true,
        photoAdded,
      };
      this.createInquiryAndStream(content, uploadedFileId, fileType);
    } else {
      this.analytics.trackAiMessageSent({
        message: content,
        previousAiMessageLength,
        customMessage: true,
        firstMessage: false,
        chatId: this.inquiryId,
        photoAdded,
      });
      this.streamTurn({ inquiryId: this.inquiryId, inquiryContent: content, uploadedFileId, fileType }, 0);
    }
  }

  private flushPendingMessageSent(chatId: string): void {
    const pending = this.pendingMessageSent;
    if (!pending) {
      return;
    }
    this.pendingMessageSent = null;
    this.analytics.trackAiMessageSent({
      message: pending.message,
      previousAiMessageLength: pending.previousAiMessageLength,
      customMessage: pending.customMessage,
      firstMessage: true,
      chatId,
      photoAdded: pending.photoAdded,
    });
  }

  private createInquiryAndStream(
    content: string,
    uploadedFileId?: string,
    fileType?: string,
    inquiryTypeId?: string
  ): void {
    this.sendingMessage = true;
    this.showTypingIndicator = true;

    const command = new CreateInquiryCommandV2();
    command.inquiryTypeId = inquiryTypeId || this.defaultInquiryTypeId;
    command.platform = 'Web';
    command.inquiryContent = content;
    command.uploadedFileId = uploadedFileId;
    command.fileType = fileType;

    this.inquiryProxy.inquiry_CreateV2(command).subscribe({
      next: (res) => {
        const newInquiryId = res.result;
        if (!newInquiryId) {
          this.handleCreateFailure(content, uploadedFileId, fileType);
          return;
        }
        this.suppressNextInquiryIdChange = true;
        this.inquiryId = newInquiryId;
        this.chatCreated.emit(newInquiryId);
        this.flushPendingMessageSent(newInquiryId);
        this.emitInquiryStatus(newInquiryId, INQUIRY_STATUS.Thinking);
        this.streamTurn({ inquiryId: newInquiryId, inquiryContent: content, uploadedFileId, fileType }, 0);
      },
      error: () => this.handleCreateFailure(content, uploadedFileId, fileType),
    });
  }

  private handleCreateFailure(content: string, uploadedFileId?: string, fileType?: string): void {
    this.sendingMessage = false;
    this.showTypingIndicator = false;
    this.removeOptimisticAiMessage();
    this.markLastUserMessageErrored();
    this.pendingRetry = { createFirst: true, request: null, content, uploadedFileId, fileType };
    // No chat was created, so the message is still reported but without an id.
    this.flushPendingMessageSent('');
    this.analytics.trackAiMessageReceived({
      chatId: '',
      surface: this.surface,
      ticketCountBefore: 0,
      aiMessageLength: 0,
      errored: true,
    });
  }

  retryLastTurn(): void {
    if (!this.pendingRetry) {
      return;
    }
    const retry = this.pendingRetry;
    this.pendingRetry = null;
    this.streamErrorMessageId = null;
    this.appendOptimisticAiPlaceholder();
    this.scrollToBottom(true);

    if (retry.createFirst) {
      this.createInquiryAndStream(retry.content, retry.uploadedFileId, retry.fileType);
    } else if (retry.request) {
      this.streamTurn(retry.request, 0);
    }
  }

  private streamTurn(request: InquiryStreamV2Request, attempt: number): void {
    this.cancelStreamSubscription();
    this.stopTypewriter();
    // Retries continue the same turn, so only attempt 0 opens a new analytics record.
    if (attempt === 0) {
      this.currentTurn = {
        chatId: request.inquiryId,
        surface: this.surface,
        ticketCountBefore: this.inquiry.ticketCount ?? 0,
        aiMessageLength: 0,
        errored: false,
      };
      this.currentTurnAwaitingReconcile = false;
    }
    this.isStreaming = true;
    this.emitInquiryStatus(request.inquiryId, INQUIRY_STATUS.Thinking);
    this.showTypingIndicator = true;
    this.firstChunkReceived = false;
    this.sendingMessage = true;
    this.streamClosed = false;
    this.displayedContent = '';
    this.aiBuffer = '';

    let erroredDuringStream = false;

    this.streamSubscription = this.inquiryStreamV2Service.stream(request).subscribe({
      next: (event) => this.handleStreamEvent(event, () => (erroredDuringStream = true)),
      error: () => {
        this.streamClosed = true;
        this.handleStreamFailure(request, attempt);
      },
      complete: () => {
        this.streamClosed = true;
        if (erroredDuringStream) {
          this.handleStreamFailure(request, attempt);
          return;
        }
        this.waitForTypewriterThen(() => this.finalizeStreamSuccess());
      },
    });
  }

  private handleStreamEvent(event: InquiryStreamV2Event, markErrored: () => void): void {
    switch (event.type) {
      case 'metadata':
        break;
      case 'chunk':
        if (!this.firstChunkReceived) {
          this.firstChunkReceived = true;
          this.showTypingIndicator = false;
        }
        this.aiBuffer += event.content ?? '';
        this.ensureTypewriter();
        break;
      case 'ui_action': {
        const action = this.findSupportedUiAction(event.actions);
        if (action && this.currentTurn) {
          this.currentTurn.uiActionType = action.actionType;
        }
        if (action && this.currentOptimisticAiId) {
          this.pendingUiAction = {
            actionType: action.actionType,
            reason: action.reason,
            messageId: this.currentOptimisticAiId,
          };
          this.selectedUiActionId = null;
          this.loadDataForUiAction(action.actionType);
        }
        break;
      }
      case 'reference':
        break;
      case 'error':
        markErrored();
        break;
      case 'done':
        break;
    }
  }

  private ensureTypewriter(): void {
    if (this.typewriterTimer) {
      return;
    }
    this.typewriterTimer = setInterval(() => this.tickTypewriter(), TYPEWRITER_TICK_MS);
  }

  private stopTypewriter(): void {
    if (this.typewriterTimer) {
      clearInterval(this.typewriterTimer);
      this.typewriterTimer = null;
    }
  }

  private tickTypewriter(): void {
    const backlog = this.aiBuffer.length - this.displayedContent.length;
    if (backlog <= 0) {
      if (this.streamClosed) {
        this.stopTypewriter();
      }
      return;
    }

    const catchupMultiplier = Math.max(1, Math.ceil(backlog / TYPEWRITER_MAX_CATCHUP_LAG));
    const charsThisTick = Math.min(backlog, TYPEWRITER_BASE_CHARS_PER_TICK * catchupMultiplier);

    this.displayedContent = this.aiBuffer.slice(0, this.displayedContent.length + charsThisTick);
    this.updateStreamingAiContent(this.displayedContent);
    this.scrollToBottom(false);
  }

  private waitForTypewriterThen(callback: () => void): void {
    if (!this.typewriterTimer && this.displayedContent.length >= this.aiBuffer.length) {
      callback();
      return;
    }
    const waitInterval = setInterval(() => {
      if (this.displayedContent.length >= this.aiBuffer.length) {
        clearInterval(waitInterval);
        this.stopTypewriter();
        callback();
      }
    }, TYPEWRITER_TICK_MS);
  }

  private handleStreamFailure(request: InquiryStreamV2Request, attempt: number): void {
    this.cancelStreamSubscription();

    const nextAttempt = attempt + 1;
    if (nextAttempt < MAX_STREAM_ATTEMPTS) {
      const delay = STREAM_RETRY_BACKOFF_MS[attempt] ?? 2000;
      this.stopTypewriter();
      this.aiBuffer = '';
      this.displayedContent = '';
      this.updateStreamingAiContent('');
      this.showTypingIndicator = true;
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null;
        this.streamTurn(request, nextAttempt);
      }, delay);
      return;
    }

    this.removeOptimisticAiMessage();
    this.resetStreamingFlags();
    this.markLastUserMessageErrored();
    this.pendingRetry = { createFirst: false, request, content: request.inquiryContent };
    if (this.currentTurn) {
      this.currentTurn.errored = true;
      this.currentTurn.aiMessageLength = 0;
    }
    this.flushCurrentTurn();
  }

  private finalizeStreamSuccess(): void {
    if (this.currentTurn) {
      // Read before resetStreamingFlags() clears the buffer.
      this.currentTurn.aiMessageLength = this.aiBuffer.length;
    }
    this.resetStreamingFlags();
    if (this.inquiryId) {
      // Held back until the reconcile below lands, because a freshly opened
      // support ticket is only visible on the reloaded inquiry's ticket count.
      this.currentTurnAwaitingReconcile = true;
      this.emitInquiryStatus(this.inquiryId, INQUIRY_STATUS.Read);
      this.chatActivity.emit(this.inquiryId);
      this.loadInquiry(true);
    } else {
      this.flushCurrentTurn();
    }
  }

  private flushCurrentTurn(ticketCountAfter?: number): void {
    const turn = this.currentTurn;
    if (!turn) {
      return;
    }
    this.currentTurn = null;
    this.currentTurnAwaitingReconcile = false;
    this.analytics.trackAiMessageReceived(turn, ticketCountAfter);
  }

  private emitInquiryStatus(inquiryId: string | undefined, status: InquiryStatus): void {
    if (!inquiryId) {
      return;
    }
    this.inquiryStatusChanged.emit({ inquiryId, status });
  }

  private resetStreamingFlags(): void {
    this.stopTypewriter();
    this.isStreaming = false;
    this.showTypingIndicator = false;
    this.firstChunkReceived = false;
    this.sendingMessage = false;
    this.currentOptimisticAiId = null;
    this.currentOptimisticUserId = null;
    this.aiBuffer = '';
    this.displayedContent = '';
    this.streamClosed = false;
  }

  private cancelStream(): void {
    this.cancelStreamSubscription();
    this.stopTypewriter();
    this.stopThinkingPoll();
    this.pollResumedTurn = false;
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    if (this.currentTurnAwaitingReconcile) {
      // The reply arrived and only the reconcile is outstanding - report it
      // rather than lose it. A turn still mid-stream is dropped instead: the
      // user abandoned it, so no message was received and no error was shown.
      this.flushCurrentTurn();
    } else {
      this.currentTurn = null;
    }
    this.pendingMessageSent = null;
    this.removeOptimisticAiMessage();
    this.resetStreamingFlags();
  }

  private cancelStreamSubscription(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = null;
    }
  }

  private markLastUserMessageErrored(): void {
    const messages = this.inquiry.inquiryCorrespondences || [];
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isByMe) {
        this.streamErrorMessageId = messages[i].id;
        break;
      }
    }
  }

  loadDataForUiAction(actionType: string): void {
    if (actionType === 'plantation.request_selection') {
      this.ensurePlantationsLoaded();
    } else if (actionType === 'inventory.request_selection') {
      this.ensureDevicesLoaded();
    }
  }

  private ensurePlantationsLoaded(): void {
    if (this.loadingPlantations || this.plantationsGroupedByLands.length > 0) {
      return;
    }
    this.loadPlantations();
  }

  private ensureDevicesLoaded(): void {
    if (this.loadingDevices || this.devices.length > 0) {
      return;
    }
    this.loadDevices();
  }

  loadPlantations(): void {
    if (this.plantationService.landList?.length > 0) {
      this.buildPlantationGroups(this.plantationService.landList);
      return;
    }

    this.loadingPlantations = true;
    this.plantationsGroupedByLands = [];
    this.plantationService.getUserPlanatation().subscribe({
      next: () => {
        this.buildPlantationGroups(this.plantationService.landList);
        this.loadingPlantations = false;
      },
      error: () => {
        this.loadingPlantations = false;
      },
    });
  }

  private buildPlantationGroups(landList: ExtendedLandViewModel[]): void {
    this.plantationsGroupedByLands = (landList || []).map((land) => ({
      label: land.name,
      value: land.id,
      items: (land.plantations || []).map((plantation) => ({
        value: {
          plantationId: plantation.id,
          name: plantation.name,
          image: plantation.image,
          cropColor: plantation.cropColor,
          cropName: plantation.crop?.value ?? '',
          landName: land.name,
          totalAreaDisplay: plantation.totalAreaDisplay,
          harvestDateDisplay: plantation.harvestEndDateDisplay,
          selected: false,
        },
        label: plantation.name,
      })),
    }));
  }

  loadDevices(): void {
    this.loadingDevices = true;
    this.devices = [];
    const userId = this.userService.currentUser?.id;
    if (!userId) {
      this.loadingDevices = false;
      return;
    }
    this.inventoryProxy.inventory_GetUserInventory(userId).subscribe({
      next: (res) => {
        (res.result || []).forEach((device) =>
          this.devices.push({
            label: device.name,
            value: { deviceId: device.id, name: device.name, productionId: device.productionId },
          })
        );
        this.loadingDevices = false;
      },
      error: () => {
        this.loadingDevices = false;
      },
    });
  }

  getResolvedPlantation(plantationId: string | undefined): IChatPlantation | undefined {
    if (!plantationId) {
      return undefined;
    }
    for (const group of this.plantationsGroupedByLands) {
      const found = group.items?.find((item) => item.value.plantationId === plantationId);
      if (found) {
        return found.value;
      }
    }
    return undefined;
  }

  getResolvedDevice(deviceId: string | undefined): IChatDevice | undefined {
    if (!deviceId) {
      return undefined;
    }
    return this.devices.find((item) => item.value.deviceId === deviceId)?.value;
  }

  onPlantationSelectedForUiAction(event: { value?: IChatPlantation }): void {
    const plantationId = event?.value?.plantationId;
    if (!plantationId) {
      return;
    }
    this.selectedUiActionId = plantationId;
    this.confirmUiActionSelection(plantationId);
  }

  onDeviceSelectedForUiAction(event: { value?: IChatDevice }): void {
    const deviceId = event?.value?.deviceId;
    if (!deviceId) {
      return;
    }
    this.selectedUiActionId = deviceId;
    this.confirmUiActionSelection(deviceId);
  }

  private confirmUiActionSelection(selectedId: string): void {
    if (!this.inquiryId || !this.pendingUiAction) {
      return;
    }
    const { actionType, messageId } = this.pendingUiAction;
    this.resolvedUiActionSelections[messageId] = { actionType, selectedId };
    this.pendingUiAction = null;
    this.appendOptimisticAiPlaceholder();
    this.scrollToBottom(true);

    const request: InquiryStreamV2Request = {
      inquiryId: this.inquiryId,
      inquiryContent: '',
      uiActionSelection: { type: actionType, selectedId },
    };
    this.streamTurn(request, 0);
  }

  openAttachPopover(target: any): void {
    if (this.uploadFilePopover) {
      this.uploadFilePopover.toggle(target);
    }
  }

  customUploadHandler(event: FileUploadHandlerEvent): void {
    const file = event.files[0];
    const fileParameter: FileParameter = { data: file, fileName: file.name };

    this.fileProxy.file_Upload(this.inquiryFileUploadTypeId, fileParameter).subscribe({
      next: (res) => {
        this.uploadFilePopover.hide();
        this.uploadedFileId = res.result?.id || '';
        this.uploadedFile = file;
        this.messageHasUploadedFile = true;
        event.files.splice(0, event.files.length);
      },
    });
  }

  private getUploadedFilePreviewUrl(): string | undefined {
    if (!this.uploadedFile || !this.uploadedFile.size) {
      return undefined;
    }
    try {
      return URL.createObjectURL(this.uploadedFile);
    } catch {
      return undefined;
    }
  }

  clearMessage(): void {
    this.messageHasUploadedFile = false;
    this.uploadedFileId = '';
    this.uploadedFile = new File([], '');
    this.messageForm.get('message')?.setValue('');
    this.messageForm.markAsUntouched();
  }

  private appendOptimisticUserMessage(content: string, fileType: string | undefined, fileUrl: string | undefined): void {
    const id = `${OPTIMISTIC_USER_PREFIX}${this.generateLocalId()}`;
    const message = new InquiryCorrespondenceDTO();
    message.id = id;
    message.inquiryContent = content;
    (message as { fileType?: string }).fileType = fileType;
    message.fileUrl = fileUrl;
    message.userId = '';
    message.userName = '';
    message.correspondenceType = 'User';
    message.correspondenceTypeId = '';
    message.forwardedToExpert = false;
    message.isByMe = true;
    message.isRetry = false;
    message.createdAt = DateTime.utc();

    this.inquiry.inquiryCorrespondences = [...(this.inquiry.inquiryCorrespondences || []), message];
    this.currentOptimisticUserId = id;
  }

  private appendOptimisticAiPlaceholder(): void {
    const id = `${OPTIMISTIC_AI_PREFIX}${this.generateLocalId()}`;
    const message = new InquiryCorrespondenceDTO();
    message.id = id;
    message.inquiryContent = '';
    message.userId = '';
    message.userName = '';
    message.correspondenceType = 'AI';
    message.correspondenceTypeId = '';
    message.forwardedToExpert = false;
    message.isByMe = false;
    message.isRetry = false;
    message.createdAt = DateTime.utc();

    this.inquiry.inquiryCorrespondences = [...(this.inquiry.inquiryCorrespondences || []), message];
    this.currentOptimisticAiId = id;
    this.renderedContents[id] = this.sanitizer.bypassSecurityTrustHtml('');
    this.aiBuffer = '';
  }

  private updateStreamingAiContent(content: string): void {
    if (!this.currentOptimisticAiId) {
      return;
    }
    const message = this.inquiry.inquiryCorrespondences?.find((m) => m.id === this.currentOptimisticAiId);
    if (!message) {
      return;
    }
    message.inquiryContent = content;
    const html = marked.parse(content || '') as string;
    this.renderedContents[message.id] = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private removeOptimisticAiMessage(): void {
    if (!this.currentOptimisticAiId) {
      return;
    }
    const id = this.currentOptimisticAiId;
    if (this.inquiry?.inquiryCorrespondences) {
      this.inquiry.inquiryCorrespondences = this.inquiry.inquiryCorrespondences.filter((m) => m.id !== id);
    }
    delete this.renderedContents[id];
    this.currentOptimisticAiId = null;
  }

  private generateLocalId(): string {
    const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private scrollToBottom(force: boolean = false): void {
    if (!this.messagesContainer?.nativeElement) {
      return;
    }
    const element = this.messagesContainer.nativeElement;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;

    if (force || isNearBottom) {
      setTimeout(() => {
        element.scrollTop = element.scrollHeight;
      }, 0);
    }
  }

  private processMarkdownForMessages(): void {
    const preserved: { [key: string]: SafeHtml } = {};
    if (this.currentOptimisticAiId && this.renderedContents[this.currentOptimisticAiId]) {
      preserved[this.currentOptimisticAiId] = this.renderedContents[this.currentOptimisticAiId];
    }

    this.renderedContents = {};
    (this.inquiry.inquiryCorrespondences || []).forEach((message) => {
      if (message.correspondenceType === 'AI' && message.inquiryContent) {
        const html = marked.parse(message.inquiryContent) as string;
        this.renderedContents[message.id] = this.sanitizer.bypassSecurityTrustHtml(html);
      }
    });

    Object.assign(this.renderedContents, preserved);
  }

  shouldShowDateSeparator(index: number): boolean {
    const messages = this.inquiry.inquiryCorrespondences || [];
    if (index === 0) {
      return true;
    }
    const current = messages[index]?.createdAt;
    const previous = messages[index - 1]?.createdAt;
    if (!current?.isValid) {
      return false;
    }
    return !previous?.isValid || !current.hasSame(previous, 'day');
  }

  formatDateSeparator(createdAt: DateTime | undefined): string {
    if (!createdAt?.isValid) {
      return '';
    }
    const locale = this.languageInitializerService.getLocaleFromISO1();
    return createdAt.toLocal().setLocale(locale).toLocaleString({
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  formatMessageTime(createdAt: DateTime | undefined): string {
    if (!createdAt?.isValid) {
      return '';
    }
    return createdAt.toLocal().toFormat('HH:mm');
  }

  shouldShowAiDisclaimer(message: InquiryCorrespondenceDTO): boolean {
    if (message.isByMe || message.correspondenceType !== 'AI') {
      return false;
    }

    if (message.id === this.currentOptimisticAiId) {
      return false;
    }

    if (this.isStreaming) {
      const messages = this.inquiry.inquiryCorrespondences ?? [];
      const last = messages[messages.length - 1];
      if (last?.id === message.id) {
        return false;
      }
    }

    return true;
  }

  get canSendMessage(): boolean {
    const hasMessage = ((this.messageForm.get('message')?.value as string) || '').trim().length > 0;
    const hasRequiredInquiryType = !!this.inquiryId || !!this.defaultInquiryTypeId;
    return (
      hasMessage &&
      hasRequiredInquiryType &&
      !this.sendingMessage &&
      !this.loadingInquiry &&
      !this.loadError &&
      !this.isStreaming &&
      !this.pendingUiAction &&
      !this.pendingRetry
    );
  }
}
